import React, { useState, useEffect } from 'react';
import { Typography, Box, IconButton, CircularProgress, useMediaQuery, useTheme, Snackbar, Alert } from '@mui/material';
import { FiArrowLeft } from 'react-icons/fi';
import { DataGrid } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import api from '../../../api';
import { FiCheckCircle, FiXCircle } from 'react-icons/fi'; 


const ExamList = ({ filterStatus }) => {
   const icon = filterStatus === 'active'
    ? <FiCheckCircle color="#2e7d32" size={30} />
    : <FiXCircle color="#d32f2f" size={30} />;
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    api.get('/exams')
      .then(res => setExams(res.data))
      .catch(err => { setError("Failed to load exams"); setOpen(true); })
      .finally(() => setLoading(false));
  }, []);

  const filteredExams = exams.filter(exam =>
    filterStatus === 'active' ? exam.status === true : exam.status === false
  );

  const columns = [
    { field: 'id', headerName: 'ID', width: 80 },
    { field: 'title', headerName: 'Exam Title', flex: 1 },
    { field: 'total_marks', headerName: 'Total Marks', flex: 0.6 },
    { field: 'passing_marks', headerName: 'Passing Marks', flex: 0.6 },
    { field: 'duration_minutes', headerName: 'Duration (min)', flex: 0.6 },
    { field: 'exam_type', headerName: 'Type', flex: 0.6 },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <span style={{ color: params.value ? 'green' : 'red', fontWeight: 600 }}>
          {params.value ? 'Active' : 'Inactive'}
        </span>
      )
    }
  ];

  return (
    <Box p={2} sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box mb={3} display="flex" flexDirection={isSmallScreen ? 'column' : 'row'} justifyContent="space-between" alignItems="center" gap={2} textAlign={isSmallScreen ? 'center' : 'left'}>
        <Box display="flex" alignItems="center" justifyContent={isSmallScreen ? 'center' : 'flex-start'}>
          <IconButton onClick={() => navigate(-1)} sx={{
            borderRadius: '12px',
            p: '8px 12px',
            background: 'linear-gradient(135deg, #00bcd4, #007bff)',
            color: '#fff',
            '&:hover': { background: 'linear-gradient(135deg, #007bff, #00bcd4)', transform: 'scale(1.05)' }
          }}>
            <FiArrowLeft />
            {!isSmallScreen && <Typography variant="button" sx={{ ml: 1, fontWeight: 600 }}>Back</Typography>}
          </IconButton>
        </Box>

        <Box display="flex" alignItems="center" justifyContent="center" gap={1} flex={1}>
          <Box sx={{   
            background: '#e3f2fd',
             borderRadius: '50%', p: 1, 
             animation: 'pulseIcon 1.6s infinite' }}>
            {icon}
          </Box>
          <Typography variant={isSmallScreen ? 'h6' : 'h5'} sx={{
            fontWeight: 'bold',
            background: 'linear-gradient(90deg, #007BFF, #00d4ff)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            textTransform: 'uppercase',
            letterSpacing: '1.2px',
            px: 1
          }}>
            {filterStatus === 'active' ? 'Active Exams' : 'Inactive Exams'}
          </Typography>
        </Box>
      </Box>

      {/* DataGrid */}
      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>
      ) : (
        <Box sx={{ width: '100%', flexGrow: 1, minHeight: 0, backgroundColor: '#fff', borderRadius: 2 }}>
          <DataGrid
            rows={filteredExams.map((exam, index) => ({
              id: exam.id || index,
              title: exam.title,
              duration_minutes: exam.duration_minutes,
              total_marks: exam.total_marks,
              passing_marks: exam.passing_marks,
              exam_type: exam.exam_type,
              status: exam.status
            }))}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
            sx={{
              minHeight: 0,
              fontFamily: 'Segoe UI',
              border: '1px solid #ddd',
              boxShadow: 2,
              '& .MuiDataGrid-columnHeaders': { background: 'linear-gradient(to right, #e0f3ff, #d5faff)', color: '#004c99', fontWeight: 'bold', fontSize: '1rem' },
              '& .MuiDataGrid-row:hover': { backgroundColor: '#fef7e0' },
              '& .MuiDataGrid-cell': { fontSize: '0.95rem' }
            }}
          />
        </Box>
      )}

      <Snackbar open={open} autoHideDuration={4000} onClose={() => setOpen(false)}>
        <Alert severity="error" onClose={() => setOpen(false)}>{error}</Alert>
      </Snackbar>
    </Box>
  );
};

export default ExamList;
